// Serialize only the first mount of persona stream charts. This queue does not
// request tickets or change Tableau authentication, filters, or retry behavior.
export function createInitialChartQueue({ setTimer = setTimeout, clearTimer = clearTimeout, requestFrame = requestAnimationFrame, cancelFrame = cancelAnimationFrame, timeout = 25000, warmup = 120 } = {}) {
  const waiting = [];
  let running = null, frame = null, warmTimer = null, warmReady = false, sequence = 0;
  function cancelWarmup() {
    if (warmTimer !== null) clearTimer(warmTimer);
    warmTimer = null;
  }
  function scheduleFrame() {
    if (frame !== null || running || !waiting.length) return;
    frame = requestFrame(() => { frame = null; pump(); });
  }
  function reschedule() {
    cancelWarmup();
    warmReady = false;
    if (!waiting.length && frame !== null) { cancelFrame(frame); frame = null; }
    scheduleFrame();
  }
  function finish(entry, advance = true) {
    if (entry.status === 'done') return;
    const index = waiting.indexOf(entry);
    if (index !== -1) waiting.splice(index, 1);
    if (entry.timer !== null) clearTimer(entry.timer);
    if (running === entry) running = null;
    entry.status = 'done';
    entry.onFinish(entry);
    if (advance) reschedule();
  }
  function pump() {
    if (running || !waiting.length) return;
    // Read live geometry after all IntersectionObserver callbacks in the frame.
    // A neighboring chart must not win just because its observer ran first.
    const candidates = [];
    waiting.slice().forEach(entry => {
      const priority = entry.priority();
      if (!priority) { finish(entry, false); return; }
      candidates.push({ entry, visible: !!priority.visible, distance: Number.isFinite(priority.distance) ? Math.max(0, priority.distance) : Infinity });
    });
    if (!candidates.length) return;
    candidates.sort((a, b) => Number(b.visible) - Number(a.visible) || a.distance - b.distance || a.entry.sequence - b.entry.sequence);
    const next = candidates[0];
    // Offscreen preloading yields briefly to a newly visible chart. Scrolling
    // resets this one-shot idle delay; it never creates a polling/RAF loop.
    if (!next.visible && !warmReady) {
      warmTimer = setTimer(() => { warmTimer = null; warmReady = true; scheduleFrame(); }, warmup);
      return;
    }
    const entry = next.entry;
    waiting.splice(waiting.indexOf(entry), 1);
    cancelWarmup();
    warmReady = false;
    running = entry;
    entry.status = 'running';
    entry.timer = setTimer(() => finish(entry), timeout);
    try { entry.onStart(entry); } catch (error) { finish(entry); throw error; }
  }
  return {
    enqueue(onStart, onFinish = () => {}, priority = () => ({ visible: true, distance: 0 })) {
      const entry = { status: 'queued', timer: null, onStart, onFinish, priority, sequence: ++sequence, release: null, reprioritize: null };
      entry.release = () => finish(entry);
      entry.reprioritize = () => { if (entry.status === 'queued') reschedule(); };
      waiting.push(entry);
      reschedule();
      return entry;
    }
  };
}

export const initialChartQueue = createInitialChartQueue();
